import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Trash2,
  Plus,
  ExternalLink,
  RotateCcw,
  Package,
  Pause,
  Play
} from 'lucide-react'
import { productAI } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface QueueJob {
  id: number
  name: string
  reference?: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error_message?: string
  result_product_id?: number
  result_reference?: string
  created_at: string
  started_at?: string
  completed_at?: string
  ai_model?: string
}

interface QueueStats {
  pending: number
  processing: number
  completed: number
  failed: number
  total: number
}

const statusConfig = {
  pending: {
    label: 'Pendiente',
    icon: Clock,
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-800 dark:text-yellow-200',
    iconColor: 'text-yellow-500',
  },
  processing: {
    label: 'Procesando',
    icon: Loader2,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-800 dark:text-blue-200',
    iconColor: 'text-primary-500',
    animate: true,
  },
  completed: {
    label: 'Completado',
    icon: CheckCircle,
    bgColor: 'bg-success-100 dark:bg-green-900/30',
    textColor: 'text-green-800 dark:text-green-200',
    iconColor: 'text-success-500',
  },
  failed: {
    label: 'Error',
    icon: XCircle,
    bgColor: 'bg-danger-100 dark:bg-danger-900/30',
    textColor: 'text-red-800 dark:text-red-200',
    iconColor: 'text-danger-500',
  },
}

function StatusBadge({ status }: { status: QueueJob['status'] }) {
  const config = statusConfig[status]
  const Icon = config.icon

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
    >
      <Icon
        className={`w-3.5 h-3.5 ${config.iconColor} ${config.animate ? 'animate-spin' : ''}`}
      />
      {config.label}
    </span>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number
  icon: React.ElementType
  color: string
}) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatRelativeTime(dateString: string | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'hace unos segundos'
  if (diffMins < 60) return `hace ${diffMins} min`
  if (diffHours < 24) return `hace ${diffHours}h`
  return `hace ${diffDays}d`
}

export default function ProductQueuePage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<'all' | 'pending' | 'processing' | 'completed' | 'failed'>('all')

  // Query para estadísticas
  const { data: stats, isLoading: statsLoading } = useQuery<QueueStats>({
    queryKey: ['queue-stats'],
    queryFn: productAI.getQueueStats,
    refetchInterval: 5000, // Refrescar cada 5 segundos
  })

  // Query para trabajos
  const { data: jobsData, isLoading: jobsLoading, refetch } = useQuery<{ jobs: QueueJob[] }>({
    queryKey: ['queue-jobs'],
    queryFn: () => productAI.getQueueJobs(100),
    refetchInterval: 5000,
  })

  // Mutation para eliminar trabajo
  const deleteMutation = useMutation({
    mutationFn: productAI.deleteQueueJob,
    onSuccess: () => {
      toast.success('Trabajo eliminado')
      queryClient.invalidateQueries({ queryKey: ['queue-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['queue-stats'] })
    },
    onError: (error: any) => {
      toast.error('Error eliminando trabajo', error.message)
    },
  })

  // Mutation para reintentar trabajo
  const retryMutation = useMutation({
    mutationFn: productAI.retryQueueJob,
    onSuccess: () => {
      toast.success('Trabajo reencolado')
      queryClient.invalidateQueries({ queryKey: ['queue-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['queue-stats'] })
    },
    onError: (error: any) => {
      toast.error('Error reintentando trabajo', error.message)
    },
  })

  // Query para estado de pausa
  const { data: pauseStatus } = useQuery({
    queryKey: ['queue-pause-status'],
    queryFn: productAI.getQueuePauseStatus,
    refetchInterval: 5000,
  })

  // Mutation para pausar cola
  const pauseMutation = useMutation({
    mutationFn: productAI.pauseQueue,
    onSuccess: (data) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['queue-pause-status'] })
    },
    onError: (error: any) => {
      toast.error('Error pausando cola', error.message)
    },
  })

  // Mutation para reanudar cola
  const resumeMutation = useMutation({
    mutationFn: productAI.resumeQueue,
    onSuccess: (data) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['queue-pause-status'] })
    },
    onError: (error: any) => {
      toast.error('Error reanudando cola', error.message)
    },
  })

  const isPaused = pauseStatus?.is_paused ?? false

  const jobs = jobsData?.jobs || []
  const filteredJobs = filter === 'all' ? jobs : jobs.filter((job) => job.status === filter)

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Cola de Creacion de Productos
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Gestiona los trabajos de creacion de productos con IA
          </p>
        </div>
        <div className="flex gap-3">
          {isPaused ? (
            <button
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              Reanudar
            </button>
          ) : (
            <button
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
            >
              <Pause className="w-4 h-4" />
              Pausar
            </button>
          )}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
          <Link
            to="/products/create"
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuevo Producto
          </Link>
        </div>
      </div>

      {/* Paused Banner */}
      {isPaused && (
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-center gap-3">
          <div className="p-2 bg-yellow-100 dark:bg-yellow-800 rounded-lg">
            <Pause className="w-5 h-5 text-yellow-600 dark:text-yellow-300" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
              Cola en pausa
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              El procesamiento de nuevos trabajos está detenido. Los trabajos pendientes se mantienen en espera.
            </p>
          </div>
          <button
            onClick={() => resumeMutation.mutate()}
            disabled={resumeMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            Reanudar ahora
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700 animate-pulse"
            >
              <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard label="Pendientes" value={stats?.pending || 0} icon={Clock} color="bg-yellow-500" />
          <StatCard label="Procesando" value={stats?.processing || 0} icon={Loader2} color="bg-blue-500" />
          <StatCard label="Completados" value={stats?.completed || 0} icon={CheckCircle} color="bg-success-500" />
          <StatCard label="Errores" value={stats?.failed || 0} icon={XCircle} color="bg-danger-500" />
          <StatCard label="Total" value={stats?.total || 0} icon={Package} color="bg-slate-500" />
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {[
          { value: 'all', label: 'Todos' },
          { value: 'pending', label: 'Pendientes' },
          { value: 'processing', label: 'Procesando' },
          { value: 'completed', label: 'Completados' },
          { value: 'failed', label: 'Errores' },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value as typeof filter)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === tab.value
                ? 'bg-primary-600 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Jobs List */}
      {jobsLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700 animate-pulse"
            >
              <div className="h-20 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-12 text-center border border-slate-200 dark:border-slate-700">
          <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            No hay trabajos {filter !== 'all' ? `con estado "${statusConfig[filter]?.label}"` : ''}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            Los trabajos de creacion de productos apareceran aqui
          </p>
          <Link
            to="/products/create"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Crear Producto con IA
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <StatusBadge status={job.status} />
                    {job.reference && (
                      <span className="text-sm font-mono text-slate-500 dark:text-slate-400">
                        {job.reference}
                      </span>
                    )}
                    <span className="text-xs text-slate-400" title={formatDate(job.created_at)}>
                      {formatRelativeTime(job.created_at)}
                    </span>
                  </div>
                  <h3 className="font-medium text-slate-900 dark:text-white truncate mb-1">
                    {job.name}
                  </h3>
                  {job.error_message && (
                    <p className="text-sm text-danger-600 dark:text-danger-400 mt-2 bg-danger-50 dark:bg-danger-900/20 p-2 rounded">
                      {job.error_message}
                    </p>
                  )}
                  {job.result_product_id && (
                    <p className="text-sm text-success-600 dark:text-success-400 mt-2">
                      Producto creado: ID {job.result_product_id}
                      {job.result_reference && ` (${job.result_reference})`}
                    </p>
                  )}
                  {job.ai_model && (
                    <p className="text-xs text-slate-400 mt-1">
                      Modelo: {job.ai_model}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {job.status === 'completed' && job.result_product_id && (
                    <a
                      href={`https://itsysshop.com/es/admin-dev/index.php?controller=AdminProducts&id_product=${job.result_product_id}&updateproduct&token=`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      title="Ver en PrestaShop"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {job.status === 'failed' && (
                    <button
                      onClick={() => retryMutation.mutate(job.id)}
                      disabled={retryMutation.isPending}
                      className="p-2 text-slate-400 hover:text-primary-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
                      title="Reintentar"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  {(job.status === 'pending' || job.status === 'failed') && (
                    <button
                      onClick={() => {
                        if (confirm('¿Eliminar este trabajo?')) {
                          deleteMutation.mutate(job.id)
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="p-2 text-slate-400 hover:text-danger-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
